import { unwrapOpsList, unwrapOpsObject } from '@/features/admin/utils/opsResponse';
import type {
  HistoricalData,
  RegionalStats,
  ReservoirData,
  LandslideRisk,
  DashboardStats,
  FloodAlert,
  CameraFeed,
  WeatherSnapshot,
  AffectedArea,
} from '@/features/realtime/types/realtime.types';
import { apiClient } from '@/lib/api-client';
import { baseApi } from '@/stores/api';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

import {
  fetchDashboardWeatherCatalogSnapshot,
  fetchDashboardWeatherSnapshot,
} from '../utils/fetchDashboardWeatherSnapshot';
import {
  mapAffectedAreaDto,
  mapCameraFeedDto,
  mapDashboardStatsDto,
  mapFloodAlertDto,
  mapHistoricalDto,
  mapLandslideDto,
  mapRegionalStatDto,
  mapReservoirDto,
} from '../utils/mapDashboardDto';

function emptyHistorical(year: string): HistoricalData {
  return {
    year,
    peakLevel: 0,
    rainfallTotal: 0,
    affectedArea: 0,
    affectedPop: 0,
    daysFlooded: 0,
    events: [],
  };
}

const emptyDashboardStats: DashboardStats = {
  alertCount: 0,
  sensorsOnline: 0,
  floodZones: 0,
  areaMonitored: 0,
};

function emptyWeather(p: { year: number; hour: number }): WeatherSnapshot {
  return {
    year: p.year,
    hour: p.hour,
    tempC: 0,
    windKmh: 0,
    rainfall24hMm: 0,
    humidityPct: 0,
    trendTempC: 0,
    trendRainMm: 0,
    trendHumidityPct: 0,
  };
}

export type WeatherSnapshotQueryParams = {
  year: number;
  hour: number;
  /** `openmeteo` (mặc định) hoặc snapshot DB theo `year`/`hour`. */
  source?: 'openmeteo' | 'snapshot';
};

function parseCameraFeedArg(arg: number | void | { year: number; hour: number; count?: number }): {
  limit: number;
  year?: number;
  hour?: number;
} {
  if (typeof arg === 'number') return { limit: arg };
  if (arg && typeof arg === 'object') {
    return {
      limit: arg.count ?? 10,
      year: arg.year,
      hour: arg.hour,
    };
  }
  return { limit: 10 };
}

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHistoricalData: builder.query<HistoricalData, string>({
      async queryFn(year) {
        const y = year || String(new Date().getFullYear());
        if (!shouldFetchOperationalApi()) {
          return { data: emptyHistorical(y) };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/historical',
            method: 'GET',
            params: { year: y },
          });
          const raw = unwrapOpsObject<unknown>(result.data);
          return { data: mapHistoricalDto(raw, y) };
        } catch {
          return { data: emptyHistorical(y) };
        }
      },
      providesTags: (_r, _e, year) => [{ type: 'HistoricalAnalysis', id: year || 'current' }],
    }),

    getRegionalStats: builder.query<RegionalStats[], { basinId?: string; year?: string } | void>({
      async queryFn(arg) {
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/regional-stats',
            method: 'GET',
            params:
              arg && typeof arg === 'object'
                ? {
                    ...(arg.basinId ? { basin_id: arg.basinId } : {}),
                    ...(arg.year ? { year: arg.year } : {}),
                  }
                : undefined,
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapRegionalStatDto(row))
            .filter((x): x is RegionalStats => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'RegionalStats', id: 'LIST' }],
    }),

    getReservoirs: builder.query<ReservoirData[], void>({
      async queryFn() {
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/reservoirs',
            method: 'GET',
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapReservoirDto(row))
            .filter((x): x is ReservoirData => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'Dashboard', id: 'RESERVOIRS' }],
    }),

    getLandslideRisks: builder.query<LandslideRisk[], void>({
      async queryFn() {
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/landslide-risks',
            method: 'GET',
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapLandslideDto(row))
            .filter((x): x is LandslideRisk => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'Dashboard', id: 'LANDSLIDE' }],
    }),

    getDashboardStats: builder.query<DashboardStats, void>({
      async queryFn() {
        if (!shouldFetchOperationalApi()) {
          return { data: emptyDashboardStats };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/stats',
            method: 'GET',
          });
          const raw = unwrapOpsObject<unknown>(result.data);
          return { data: mapDashboardStatsDto(raw) };
        } catch {
          return { data: emptyDashboardStats };
        }
      },
      providesTags: [{ type: 'DashboardStats', id: 'SUMMARY' }],
    }),

    getFloodAlerts: builder.query<FloodAlert[], number | void>({
      async queryFn(limitArg) {
        const limit = typeof limitArg === 'number' && limitArg > 0 ? limitArg : 10;
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/flood-alerts',
            method: 'GET',
            params: { limit },
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapFloodAlertDto(row))
            .filter((x): x is FloodAlert => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'Dashboard', id: 'FLOOD_ALERTS' }],
    }),

    getCameraFeeds: builder.query<
      CameraFeed[],
      number | void | { year: number; hour: number; count?: number }
    >({
      async queryFn(arg) {
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        const { limit, year, hour } = parseCameraFeedArg(arg);
        try {
          const result = await apiClient({
            url: '/api/dashboard/cameras',
            method: 'GET',
            params: {
              limit,
              ...(year != null ? { year } : {}),
              ...(hour != null ? { hour } : {}),
            },
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapCameraFeedDto(row))
            .filter((x): x is CameraFeed => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'Dashboard', id: 'CAMERAS' }],
    }),

    getWeatherSnapshot: builder.query<WeatherSnapshot, WeatherSnapshotQueryParams>({
      async queryFn(params) {
        if (!shouldFetchOperationalApi()) {
          return { data: emptyWeather(params) };
        }
        const source = params.source ?? 'openmeteo';
        try {
          if (source === 'snapshot') {
            const snap = await fetchDashboardWeatherCatalogSnapshot(params);
            if (!snap) return { data: emptyWeather(params) };
            return { data: snap };
          }
          const snap = await fetchDashboardWeatherSnapshot(params);
          if (!snap) return { data: emptyWeather(params) };
          return { data: snap };
        } catch {
          return { data: emptyWeather(params) };
        }
      },
      providesTags: ['Weather'],
    }),

    getAffectedAreas: builder.query<AffectedArea[], { year: number; hour: number }>({
      async queryFn(params) {
        if (!shouldFetchOperationalApi()) {
          return { data: [] };
        }
        try {
          const result = await apiClient({
            url: '/api/dashboard/affected-areas',
            method: 'GET',
            params: { year: params.year, hour: params.hour },
          });
          const list = unwrapOpsList<unknown>(result.data);
          const mapped = list
            .map((row) => mapAffectedAreaDto(row))
            .filter((x): x is AffectedArea => x != null);
          return { data: mapped };
        } catch {
          return { data: [] };
        }
      },
      providesTags: (_r, _e, arg) => [{ type: 'AffectedArea', id: `${arg.year}-${arg.hour}` }],
    }),

    /**
     * Nhiều năm — song song GET historical cho biểu đồ cột (Dashboard scenario historical).
     */
    getHistoricalYearlyFloodedSeries: builder.query<{ year: string; area: number }[], number[]>({
      async queryFn(years) {
        if (!shouldFetchOperationalApi() || !years.length) {
          return { data: [] };
        }
        try {
          const results = await Promise.all(
            years.map(async (y) => {
              const result = await apiClient({
                url: '/api/dashboard/historical',
                method: 'GET',
                params: { year: String(y) },
              });
              const raw = unwrapOpsObject<unknown>(result.data);
              const h = mapHistoricalDto(raw, String(y));
              return { year: h.year, area: h.affectedArea };
            }),
          );
          return { data: results.map((r) => ({ year: r.year, area: r.area })) };
        } catch {
          return { data: [] };
        }
      },
      providesTags: [{ type: 'HistoricalAnalysis', id: 'YEARLY_SERIES' }],
    }),
  }),
});

export const {
  useGetHistoricalDataQuery,
  useGetRegionalStatsQuery,
  useGetReservoirsQuery,
  useGetLandslideRisksQuery,
  useGetDashboardStatsQuery,
  useGetFloodAlertsQuery,
  useGetCameraFeedsQuery,
  useGetWeatherSnapshotQuery,
  useGetAffectedAreasQuery,
  useGetHistoricalYearlyFloodedSeriesQuery,
} = dashboardApi;
